import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react'

interface DashboardMapAnchorValue {
  anchor: HTMLDivElement | null
  setAnchor: (el: HTMLDivElement | null) => void
  // #368 — where the dashboard wants the Google map / Site map switch (#363): its map card's title row. Null = in the box.
  modeAnchor: HTMLDivElement | null
  setModeAnchor: (el: HTMLDivElement | null) => void
  onMarkerClick: ((id: number) => void) | null
  setOnMarkerClick: (fn: ((id: number) => void) | null) => void
  // #247 — which pins the dashboard page wants shown; null = all (no filter active).
  visibleIds: number[] | null
  setVisibleIds: (ids: number[] | null) => void
  // #282 — the camera whose detail dialog is open on the dashboard; its pin shows the enlarged state.
  selectedId: number | null
  setSelectedId: (id: number | null) => void
  // #290 — counts the user's edits of the map filter; the restored map view holds until one.
  filterEdits: number
  noteFilterEdit: () => void
}

const DashboardMapAnchorContext = createContext<DashboardMapAnchorValue | null>(null)

export function DashboardMapAnchorProvider({ children }: { children: ReactNode }) {
  const [anchor, setAnchorState] = useState<HTMLDivElement | null>(null)
  const [modeAnchor, setModeAnchorState] = useState<HTMLDivElement | null>(null)
  const [onMarkerClick, setOnMarkerClickState] = useState<((id: number) => void) | null>(null)
  const [visibleIds, setVisibleIdsState] = useState<number[] | null>(null)
  const [selectedId, setSelectedIdState] = useState<number | null>(null)
  const [filterEdits, setFilterEdits] = useState(0)

  // Stable setters. Without useCallback these functions get a new identity on
  // every provider render; consumer useEffects that depend on the setter (e.g.
  // DashboardPage registering its handler) would re-fire infinitely and
  // freeze the app. useCallback fixes that.
  const setAnchor = useCallback((el: HTMLDivElement | null) => {
    setAnchorState(el)
  }, [])

  const setModeAnchor = useCallback((el: HTMLDivElement | null) => setModeAnchorState(el), [])

  const setOnMarkerClick = useCallback((fn: ((id: number) => void) | null) => {
    // useState with a function value: the setter must wrap the function in another
    // function, otherwise React treats it as an initializer and calls it immediately.
    setOnMarkerClickState(() => fn)
  }, [])

  const setVisibleIds = useCallback((ids: number[] | null) => setVisibleIdsState(ids), [])
  const setSelectedId = useCallback((id: number | null) => setSelectedIdState(id), [])
  const noteFilterEdit = useCallback(() => setFilterEdits((n) => n + 1), [])

  // Memo the value so consumers that destructure are stable across unrelated
  // provider re-renders.
  const value = useMemo<DashboardMapAnchorValue>(
    () => ({ anchor, setAnchor, modeAnchor, setModeAnchor, onMarkerClick, setOnMarkerClick, visibleIds, setVisibleIds, selectedId, setSelectedId, filterEdits, noteFilterEdit }),
    [anchor, setAnchor, modeAnchor, setModeAnchor, onMarkerClick, setOnMarkerClick, visibleIds, setVisibleIds, selectedId, setSelectedId, filterEdits, noteFilterEdit]
  )

  return (
    <DashboardMapAnchorContext.Provider value={value}>
      {children}
    </DashboardMapAnchorContext.Provider>
  )
}

export function useDashboardMapAnchor() {
  const ctx = useContext(DashboardMapAnchorContext)
  if (!ctx) {
    throw new Error('useDashboardMapAnchor must be used inside DashboardMapAnchorProvider')
  }
  return ctx
}
